import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';
import adaEmbed from '@ada-support/embed2';
import { RootState, store } from 'app/store';
import type { LooseObjectType } from 'utils/globalTypes';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { getFromBrandConfig } from 'brand-config';
import { logErrors, isValidNonEmptyObj } from 'utils';
import {
  selectBrandCode,
  selectIsBrandDetailsExist,
} from 'services/brand/brandSelectors';
import { selectUserSignInStatus } from 'services/session/sessionSelectors';
import { fetchThinSessionDetails } from 'services/session/sessionAsync';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';

type WindowInfo = {
  hasActiveChatter: boolean;
  hasClosedChat: boolean;
  isChatOpen: boolean;
  isDrawerOpen: boolean;
};

type AdaChatProviderProps = {
  children: ReactNode;
};

const AdaChatContext = createContext({
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  openAdaChat(campaignName: string, payload?: LooseObjectType) {},
  isAdaChatAvailable: false,
});

function AdaChatProvider({ children }: AdaChatProviderProps) {
  const dispatch = useAppDispatch();
  const isBrandDetailsExist = useAppSelector(selectIsBrandDetailsExist);

  /* Not providing ADA Chat feature for eyeon users */
  const isAdaChatAvailable = useMemo(() => {
    if (!isBrandDetailsExist) {
      return false;
    }

    return getFromBrandConfig('adaChat');
  }, [isBrandDetailsExist]);

  const showAdaChat = useCallback((action: boolean) => {
    adaEmbed.getInfo().then((windowInfo: WindowInfo) => {
      if (windowInfo.isDrawerOpen !== action) {
        adaEmbed.toggle();
      }
    });
  }, []);

  /* Function will trigger when the Ada is ready */
  const handleAdaReady = useCallback(() => {
    showAdaChat(false);
  }, []);

  /* Send meta fields and sensitive meta fields to the ADA on chat drawer window opened */
  const handleToggle = useCallback((isDrawerOpen: boolean) => {
    if (isDrawerOpen) {
      const appStore = store.getState() as RootState;
      const brandCode = selectBrandCode(appStore);
      const planCode = selectOfferPlanCode(appStore);
      const isLogin = selectUserSignInStatus(appStore);

      const metaFields = {
        brand: brandCode,
        planCode: planCode ?? null,
        pageName: window.location.pathname,
      };
      adaEmbed.setMetaFields(metaFields);

      if (isLogin) {
        dispatch(fetchThinSessionDetails())
          .then((response) => {
            if (response?.accessToken) {
              adaEmbed.setSensitiveMetaFields({
                Ada_JWT_Token_Captured: response.accessToken,
              });
            }
          })
          .catch((error) => {
            logErrors(error);
          });
      }
    }
  }, []);

  useEffect(() => {
    if (isAdaChatAvailable) {
      adaEmbed.start({
        handle: 'pango-gr',
        adaReadyCallback: handleAdaReady,
        toggleCallback: handleToggle,
      });
    }
  }, [isAdaChatAvailable, handleAdaReady, handleToggle]);

  const openAdaChat = useCallback(
    (campaignName: string, payload?: LooseObjectType) => {
      if (isAdaChatAvailable) {
        if (isValidNonEmptyObj(payload)) {
          adaEmbed.setMetaFields({
            ...(payload ?? {}),
          });
        }

        adaEmbed.triggerCampaign(campaignName, {
          ignoreStatus: false,
          ignoreFrequency: false,
        });

        showAdaChat(true);
      }
    },
    [isAdaChatAvailable],
  );

  const value = useMemo(
    () => ({
      openAdaChat,
      isAdaChatAvailable,
    }),
    [isAdaChatAvailable, openAdaChat],
  );

  return (
    <AdaChatContext.Provider value={value}>{children}</AdaChatContext.Provider>
  );
}

export const useAdaChat = () => useContext(AdaChatContext);

export default AdaChatProvider;
